import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/24/outline'
import BulkOperationsModal from '../components/BulkOperationsModal'
import TicketFiltersModal from '../components/TicketFiltersModal'
import { useAuthStore } from '../stores/authStore'
import {
  BUILT_IN_TICKET_VIEWS,
  BuiltInTicketViewId,
  getBuiltInViewFilterPatch,
  isBuiltInTicketViewId,
} from '../utils/ticketSavedViews'
import {
  EstimatedHoursFilter,
  NO_SOURCE,
  SortDirection,
  SortField,
  TableSize,
  FilterPillRemove,
  TicketFilterState,
  UNASSIGNED_ASSIGNEE,
  buildActiveFilterPills,
  countActiveFilters,
  filterTickets,
  resolveInitialStatusSelection,
  sanitizePrioritySelections,
  sanitizeSelectedIds,
  sortTickets,
} from '../utils/ticketFilterUtils'

interface TicketStatus {
  id: string
  name: string
  color: string
}

interface Ticket {
  id: string
  ticketNumber: string
  title: string
  description: string
  status: {
    id: string
    name: string
    color: string
  }
  priority: {
    id: string
    name: string
    color: string
  }
  category: {
    id: string
    name: string
  }
  department: {
    id: string
    name: string
  }
  source?: {
    id: string
    name: string
    color?: string
  } | null
  clientOrg?: {
    id: string
    name: string
  }
  clientUser: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  assignedUser?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  estimatedHours?: number
  tags?: string[]
  metadata?: any
  createdAt: string
  updatedAt: string
}

const createDefaultFilterState = (): TicketFilterState => ({
  selectedStatuses: [],
  selectedPriorities: [],
  selectedDepartments: [],
  selectedSources: [],
  selectedAssignees: [],
  selectedClientOrgs: [],
  selectedClientUsers: [],
  tagSearch: '',
  createdFrom: '',
  createdTo: '',
  estimatedHoursFilter: 'all',
  tableSize: 'medium',
  sortField: 'createdAt',
  sortDirection: 'desc',
  showActiveFilterPills: false,
})

const Tickets: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const isStaff = user?.roles?.some(r =>
    ['administrator', 'support_staff', 'manager'].includes(r)
  )

  const [tickets, setTickets] = useState<Ticket[]>([])
  const [availableStatuses, setAvailableStatuses] = useState<TicketStatus[]>([])
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([])
  const [sources, setSources] = useState<{ id: string; name: string }[]>([])
  const [staffUsers, setStaffUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([])
  const [clientOrganizations, setClientOrganizations] = useState<any[]>([])
  const [clientUsers, setClientUsers] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
  const [selectedSources, setSelectedSources] = useState<string[]>([])
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [selectedClientOrgs, setSelectedClientOrgs] = useState<string[]>([])
  const [selectedClientUsers, setSelectedClientUsers] = useState<string[]>([])
  const [ticketSearch, setTicketSearch] = useState('')
  const [tagSearch, setTagSearch] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [estimatedHoursFilter, setEstimatedHoursFilter] = useState<EstimatedHoursFilter>('all')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedTickets, setSelectedTickets] = useState<string[]>([])
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [tableSize, setTableSize] = useState<TableSize>('medium')
  const [showActiveFilterPills, setShowActiveFilterPills] = useState(false)
  const [activeViewId, setActiveViewId] = useState<BuiltInTicketViewId | ''>('')
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [filterDraft, setFilterDraft] = useState<TicketFilterState>(createDefaultFilterState())
  const [draftClientUsers, setDraftClientUsers] = useState<any[]>([])
  const defaultStatusesInitialized = useRef(false)

  // Load saved filters
  useEffect(() => {
    try {
      const saved = localStorage.getItem('tickets.filters')
      if (saved) {
        const parsed = JSON.parse(saved)
        setSelectedStatuses(parsed.selectedStatuses || [])
        setSelectedPriorities(parsed.selectedPriorities || [])
        setSelectedDepartments(parsed.selectedDepartments || [])
        setSelectedSources(parsed.selectedSources || [])
        setSelectedAssignees(parsed.selectedAssignees || [])
        setSelectedClientOrgs(parsed.selectedClientOrgs || [])
        setSelectedClientUsers(parsed.selectedClientUsers || [])
        setTicketSearch(parsed.ticketSearch || '')
        setTagSearch(parsed.tagSearch || '')
        setCreatedFrom(parsed.createdFrom || '')
        setCreatedTo(parsed.createdTo || '')
        if (parsed.estimatedHoursFilter) setEstimatedHoursFilter(parsed.estimatedHoursFilter)
        if (parsed.sortField) setSortField(parsed.sortField)
        if (parsed.sortDirection) setSortDirection(parsed.sortDirection)
        if (parsed.tableSize) setTableSize(parsed.tableSize)
        if (typeof parsed.showActiveFilterPills === 'boolean') {
          setShowActiveFilterPills(parsed.showActiveFilterPills)
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, [])

  // Persist filters
  useEffect(() => {
    const payload = {
      selectedStatuses,
      selectedPriorities,
      selectedDepartments,
      selectedSources,
      selectedAssignees,
      selectedClientOrgs,
      selectedClientUsers,
      ticketSearch,
      tagSearch,
      createdFrom,
      createdTo,
      estimatedHoursFilter,
      sortField,
      sortDirection,
      tableSize,
      showActiveFilterPills,
    }
    localStorage.setItem('tickets.filters', JSON.stringify(payload))
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedDepartments,
    selectedSources,
    selectedAssignees,
    selectedClientOrgs,
    selectedClientUsers,
    ticketSearch,
    tagSearch,
    createdFrom,
    createdTo,
    estimatedHoursFilter,
    sortField,
    sortDirection,
    tableSize,
    showActiveFilterPills,
  ])

  const location = useLocation()

  // Helper: fetch tickets with cache-busting to avoid any intermediary caching
  const fetchTicketsNow = async () => {
    const requests: Promise<any>[] = [
      axios.get('/api/tickets', { params: { ts: Date.now(), limit: 500 } }),
      axios.get('/api/tickets/statuses', { params: { ts: Date.now() } }),
      axios.get('/api/tickets/departments', { params: { ts: Date.now() } }),
      axios.get('/api/tickets/sources', { params: { ts: Date.now() } }),
      axios.get('/api/organizations', { params: { ts: Date.now() } }),
    ]
    if (isStaff) {
      requests.push(axios.get('/api/users/staff', { params: { ts: Date.now() } }))
    }
    const results = await Promise.all(requests)
    return {
      ticketsResponse: results[0],
      statusesResponse: results[1],
      departmentsResponse: results[2],
      sourcesResponse: results[3],
      orgsResponse: results[4],
      staffResponse: isStaff ? results[5] : null,
    }
  }

  // Initial fetch
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const {
          ticketsResponse,
          statusesResponse,
          departmentsResponse,
          sourcesResponse,
          orgsResponse,
          staffResponse,
        } = await fetchTicketsNow()
        if (
          ticketsResponse.data.success &&
          statusesResponse.data.success &&
          departmentsResponse.data.success &&
          sourcesResponse.data.success &&
          orgsResponse.data.success
        ) {
          setTickets(ticketsResponse.data.data || [])
          setAvailableStatuses(statusesResponse.data.data || [])
          setDepartments(departmentsResponse.data.data || [])
          setSources(sourcesResponse.data.data || [])
          setClientOrganizations(orgsResponse.data.data || [])
          if (staffResponse?.data?.success) {
            setStaffUsers(staffResponse.data.data || [])
          }
        } else {
          setError('Failed to fetch tickets data')
        }
      } catch (error: any) {
        console.error('Error fetching tickets:', error)
        setError(error.response?.data?.error || 'Failed to fetch tickets')
        toast.error('Failed to load tickets')
      } finally {
        setIsLoading(false)
      }
    }
    fetchInitial()
  }, [isStaff])

  // Re-fetch when navigating back to /tickets (location key changes on router navigation)
  useEffect(() => {
    const refreshOnRoute = async () => {
      try {
        const tr = await axios.get('/api/tickets', { params: { ts: Date.now(), limit: 500 } })
        if (tr.data.success) setTickets(tr.data.data || [])
      } catch { /* ignore */ }
    }
    refreshOnRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  // Lightweight refresh on window focus/visibility (keeps list in sync after reassignment)
  useEffect(() => {
    let isCancelled = false
    const refreshTickets = async () => {
      try {
        const ticketsResponse = await axios.get('/api/tickets', { params: { ts: Date.now(), limit: 500 } })
        if (!isCancelled && ticketsResponse.data.success) {
          setTickets(ticketsResponse.data.data || [])
        }
      } catch (err) {
        // ignore transient errors on focus refresh
      }
    }
    const onFocus = () => refreshTickets()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshTickets()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      isCancelled = true
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  // Apply default status filter on first load; drop stale IDs from a prior DB seed
  useEffect(() => {
    if (defaultStatusesInitialized.current) return
    if (!availableStatuses.length) return

    let savedStatuses: string[] = []
    const saved = localStorage.getItem('tickets.filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        savedStatuses = Array.isArray(parsed?.selectedStatuses) ? parsed.selectedStatuses : []
      } catch {
        // Ignore parse errors
      }
    }

    setSelectedStatuses(resolveInitialStatusSelection(savedStatuses, availableStatuses))
    defaultStatusesInitialized.current = true
  }, [availableStatuses])

  // Drop stale filter IDs when reference data loads (common after docker volume / DB reset)
  useEffect(() => {
    if (!availableStatuses.length) return

    setSelectedPriorities(prev => sanitizePrioritySelections(prev))
    setSelectedDepartments(prev =>
      sanitizeSelectedIds(prev, new Set(departments.map(d => d.id)))
    )
    setSelectedSources(prev =>
      sanitizeSelectedIds(prev, new Set([...sources.map(s => s.id), NO_SOURCE]))
    )
    setSelectedClientOrgs(prev =>
      sanitizeSelectedIds(prev, new Set(clientOrganizations.map(o => o.id)))
    )

    if (!selectedClientOrgs.length) {
      setSelectedClientUsers(prev => (prev.length === 0 ? prev : []))
    } else if (clientUsers.length > 0) {
      setSelectedClientUsers(prev =>
        sanitizeSelectedIds(prev, new Set(clientUsers.map(u => u.id)))
      )
    }

    if (isStaff) {
      const validAssigneeIds = new Set([...staffUsers.map(u => u.id), UNASSIGNED_ASSIGNEE])
      setSelectedAssignees(prev => sanitizeSelectedIds(prev, validAssigneeIds))
    }
  }, [
    availableStatuses,
    departments,
    sources,
    clientOrganizations,
    staffUsers,
    clientUsers,
    selectedClientOrgs,
    isStaff,
  ])

  // Fetch client users when client organization changes (single org selection previously). For multi-select, if multiple orgs, we don't load users list.
  useEffect(() => {
    const fetchClientUsers = async () => {
      if (!selectedClientOrgs.length) {
        setClientUsers(prev => (prev.length === 0 ? prev : []))
        setSelectedClientUsers(prev => (prev.length === 0 ? prev : []))
        return
      }
      // If multiple orgs selected, skip loading users to avoid ambiguity
      if (selectedClientOrgs.length > 1) {
        setClientUsers(prev => (prev.length === 0 ? prev : []))
        setSelectedClientUsers(prev => (prev.length === 0 ? prev : []))
        return
      }
      try {
        const response = await axios.get(`/api/organizations/${selectedClientOrgs[0]}/users`)
        if (response.data.success) {
          setClientUsers(response.data.data || [])
        }
      } catch (error) {
        console.error('Error fetching client users:', error)
        setClientUsers([])
      }
    }
    fetchClientUsers()
  }, [selectedClientOrgs])

  useEffect(() => {
    if (!showFiltersModal || filterDraft.selectedClientOrgs.length !== 1) {
      setDraftClientUsers([])
      return
    }
    const orgId = filterDraft.selectedClientOrgs[0]
    const fetchDraftClientUsers = async () => {
      try {
        const response = await axios.get(`/api/organizations/${orgId}/users`)
        if (response.data.success) {
          setDraftClientUsers(response.data.data || [])
        }
      } catch (error) {
        console.error('Error fetching client users for filter preview:', error)
        setDraftClientUsers([])
      }
    }
    fetchDraftClientUsers()
  }, [showFiltersModal, filterDraft.selectedClientOrgs])

  const buildFilterState = (): TicketFilterState => ({
    selectedStatuses,
    selectedPriorities,
    selectedDepartments,
    selectedSources,
    selectedAssignees,
    selectedClientOrgs,
    selectedClientUsers,
    tagSearch,
    createdFrom,
    createdTo,
    estimatedHoursFilter,
    tableSize,
    sortField,
    sortDirection,
    showActiveFilterPills,
  })

  const filteredTickets = useMemo(() => {
    const filters = buildFilterState()
    const filtered = filterTickets(tickets, filters, {
      ticketSearch,
      assignedToMe: searchParams.get('assigned') === 'me',
      userId: user?.id,
    })
    return sortTickets(filtered, sortField, sortDirection)
  }, [
    tickets,
    selectedStatuses,
    selectedPriorities,
    selectedDepartments,
    selectedSources,
    selectedAssignees,
    selectedClientOrgs,
    selectedClientUsers,
    tagSearch,
    createdFrom,
    createdTo,
    estimatedHoursFilter,
    sortField,
    sortDirection,
    ticketSearch,
    searchParams,
    user?.id,
  ])

  const activeFilterCount = useMemo(() => countActiveFilters(buildFilterState()), [
    selectedStatuses,
    selectedPriorities,
    selectedDepartments,
    selectedSources,
    selectedAssignees,
    selectedClientOrgs,
    selectedClientUsers,
    tagSearch,
    createdFrom,
    createdTo,
    estimatedHoursFilter,
    tableSize,
    sortField,
    sortDirection,
  ])

  const draftMatchCount = useMemo(() => {
    if (!showFiltersModal) return filteredTickets.length
    return filterTickets(tickets, filterDraft, {
      ticketSearch,
      assignedToMe: searchParams.get('assigned') === 'me',
      userId: user?.id,
    }).length
  }, [showFiltersModal, tickets, filterDraft, ticketSearch, searchParams, user?.id, filteredTickets.length])

  const hasActiveFilters = activeFilterCount > 0 || ticketSearch.trim().length > 0

  const filterLabelLookup = useMemo(
    () => ({
      status: Object.fromEntries(
        availableStatuses.map(s => [s.id, s.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())])
      ),
      priority: Object.fromEntries(
        ['untracked', 'low', 'normal', 'high', 'urgent'].map(p => [p, p === 'untracked' ? 'Untracked' : p])
      ),
      department: Object.fromEntries(departments.map(d => [d.id, d.name])),
      source: Object.fromEntries([
        [NO_SOURCE, 'No source'],
        ...sources.map(s => [s.id, s.name]),
      ]),
      assignee: Object.fromEntries([
        [UNASSIGNED_ASSIGNEE, 'Unassigned'],
        ...staffUsers.map(s => [s.id, `${s.firstName} ${s.lastName}`]),
      ]),
      clientOrg: Object.fromEntries(
        clientOrganizations.filter(org => org.isActive).map(org => [org.id, org.name])
      ),
      clientUser: Object.fromEntries(
        clientUsers.map(u => [u.id, `${u.firstName} ${u.lastName}`])
      ),
    }),
    [availableStatuses, departments, sources, staffUsers, clientOrganizations, clientUsers]
  )

  const activeFilterPills = useMemo(
    () =>
      buildActiveFilterPills(buildFilterState(), filterLabelLookup, {
        ticketSearch,
      }),
    [
      selectedStatuses,
      selectedPriorities,
      selectedDepartments,
      selectedSources,
      selectedAssignees,
      selectedClientOrgs,
      selectedClientUsers,
      tagSearch,
      createdFrom,
      createdTo,
      estimatedHoursFilter,
      sortField,
      sortDirection,
      tableSize,
      ticketSearch,
      filterLabelLookup,
    ]
  )

  const statusIdByName = useMemo(
    () => Object.fromEntries(availableStatuses.map(s => [s.name, s.id])),
    [availableStatuses]
  )

  const applyBuiltInView = (viewId: BuiltInTicketViewId | '') => {
    if (!viewId) {
      setActiveViewId('')
      return
    }
    const patch = getBuiltInViewFilterPatch(viewId, {
      userId: user?.id,
      statusIdByName,
    })
    setSelectedStatuses(patch.selectedStatuses)
    setSelectedPriorities(patch.selectedPriorities)
    setSelectedDepartments(patch.selectedDepartments)
    setSelectedSources(patch.selectedSources)
    setSelectedAssignees(patch.selectedAssignees)
    setSelectedClientOrgs(patch.selectedClientOrgs)
    setSelectedClientUsers(patch.selectedClientUsers)
    setTagSearch(patch.tagSearch)
    setCreatedFrom(patch.createdFrom)
    setCreatedTo(patch.createdTo)
    setEstimatedHoursFilter(patch.estimatedHoursFilter)
    setTicketSearch('')
    setActiveViewId(viewId)
  }

  const markFiltersCustom = () => {
    if (activeViewId) setActiveViewId('')
  }

  const removeFilterPill = (remove: FilterPillRemove) => {
    markFiltersCustom()
    switch (remove.type) {
      case 'ticketSearch':
        setTicketSearch('')
        break
      case 'status':
        setSelectedStatuses(prev => prev.filter(id => id !== remove.value))
        break
      case 'priority':
        setSelectedPriorities(prev => prev.filter(id => id !== remove.value))
        break
      case 'department':
        setSelectedDepartments(prev => prev.filter(id => id !== remove.value))
        break
      case 'source':
        setSelectedSources(prev => prev.filter(id => id !== remove.value))
        break
      case 'assignee':
        setSelectedAssignees(prev => prev.filter(id => id !== remove.value))
        break
      case 'clientOrg':
        setSelectedClientOrgs(prev => prev.filter(id => id !== remove.value))
        setSelectedClientUsers([])
        break
      case 'clientUser':
        setSelectedClientUsers(prev => prev.filter(id => id !== remove.value))
        break
      case 'tagSearch':
        setTagSearch('')
        break
      case 'createdRange':
        setCreatedFrom('')
        setCreatedTo('')
        break
      case 'estimatedHours':
        setEstimatedHoursFilter('all')
        break
      case 'sort':
        setSortField('createdAt')
        setSortDirection('desc')
        break
      case 'tableSize':
        setTableSize('medium')
        break
    }
  }

  const clearFilters = () => {
    const defaults = createDefaultFilterState()
    setSelectedStatuses(defaults.selectedStatuses)
    setSelectedPriorities(defaults.selectedPriorities)
    setSelectedDepartments(defaults.selectedDepartments)
    setSelectedSources(defaults.selectedSources)
    setSelectedAssignees(defaults.selectedAssignees)
    setSelectedClientOrgs(defaults.selectedClientOrgs)
    setSelectedClientUsers(defaults.selectedClientUsers)
    setTicketSearch('')
    setTagSearch(defaults.tagSearch)
    setCreatedFrom(defaults.createdFrom)
    setCreatedTo(defaults.createdTo)
    setEstimatedHoursFilter(defaults.estimatedHoursFilter)
    setSortField(defaults.sortField)
    setSortDirection(defaults.sortDirection)
    setTableSize(defaults.tableSize)
    setShowActiveFilterPills(defaults.showActiveFilterPills)
    setActiveViewId('all')
  }

  const openFiltersModal = () => {
    setFilterDraft(buildFilterState())
    setShowFiltersModal(true)
  }

  const applyFilterDraft = () => {
    setSelectedStatuses(filterDraft.selectedStatuses)
    setSelectedPriorities(filterDraft.selectedPriorities)
    setSelectedDepartments(filterDraft.selectedDepartments)
    setSelectedSources(filterDraft.selectedSources)
    setSelectedAssignees(filterDraft.selectedAssignees)
    setSelectedClientOrgs(filterDraft.selectedClientOrgs)
    setSelectedClientUsers(filterDraft.selectedClientUsers)
    setTagSearch(filterDraft.tagSearch)
    setCreatedFrom(filterDraft.createdFrom)
    setCreatedTo(filterDraft.createdTo)
    setEstimatedHoursFilter(filterDraft.estimatedHoursFilter)
    setSortField(filterDraft.sortField)
    setSortDirection(filterDraft.sortDirection)
    setTableSize(filterDraft.tableSize)
    setShowActiveFilterPills(filterDraft.showActiveFilterPills)
    setActiveViewId('')
    setShowFiltersModal(false)
  }

  const clearFilterDraft = () => {
    setFilterDraft(createDefaultFilterState())
  }

  const handleNewTicket = () => {
    navigate('/tickets/new')
  }

  const handleRefresh = async () => {
    try {
      const ticketsResponse = await axios.get('/api/tickets', { params: { ts: Date.now(), limit: 500 } })
      if (ticketsResponse.data.success) {
        setTickets(ticketsResponse.data.data || [])
        toast.success('Tickets refreshed')
      } else {
        toast.error('Failed to refresh tickets')
      }
    } catch (err) {
      toast.error('Failed to refresh tickets')
    }
  }

  const handleTicketSelect = (ticketId: string) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    )
  }

  const handleSelectAll = () => {
    if (selectedTickets.length === filteredTickets.length) {
      setSelectedTickets([])
    } else {
      setSelectedTickets(filteredTickets.map(t => t.id))
    }
  }

  const clearSelection = () => {
    setSelectedTickets([])
  }

  const handleBulkOperation = (operation: string) => {
    setShowBulkModal(true)
  }

  const handleBulkSuccess = () => {
    // Refresh tickets after bulk operation
    window.location.reload()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (statusName: string) => {
    const statusColors: { [key: string]: string } = {
      'new': 'bg-blue-100 text-blue-800',
      'internal_analysis': 'bg-purple-100 text-purple-800',
      'work_in_progress': 'bg-green-100 text-green-800',
      'work_scheduled': 'bg-yellow-100 text-yellow-800',
      'awaiting_third_party': 'bg-red-100 text-red-800',
      'awaiting_customer_response': 'bg-purple-100 text-purple-800',
      'pending_closure': 'bg-gray-100 text-gray-800',
      'closed_complete': 'bg-green-100 text-green-800'
    }
    return statusColors[statusName] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-yellow-100 text-yellow-800'
      case 'normal':
        return 'bg-blue-100 text-blue-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTableStyles = (size: TableSize) => {
    const clamp = (maxWidth: string) => (maxWidth ? `truncate ${maxWidth}` : '')
    switch (size) {
      case 'small':
        return {
          tableClass: 'min-w-0',
          rowClass: '',
          headerPadding: 'px-2 py-1',
          cellPadding: 'px-2 py-1',
          fontSize: 'text-[11px] leading-tight',
          headerFontSize: 'text-[10px]',
          badgePadding: 'px-1 py-0.5',
          badgeFontSize: 'text-[10px]',
          titleClamp: clamp('max-w-[120px]'),
          deptClamp: clamp('max-w-[72px]'),
          userClamp: clamp('max-w-[90px]'),
          tagLimit: 1,
          checkboxClass: 'h-3.5 w-3.5',
        }
      case 'large':
        return {
          tableClass: 'min-w-[1280px]',
          rowClass: 'min-h-[4.5rem]',
          headerPadding: 'px-6 py-4',
          cellPadding: 'px-6 py-4',
          fontSize: 'text-base leading-relaxed',
          headerFontSize: 'text-sm',
          badgePadding: 'px-3 py-1.5',
          badgeFontSize: 'text-sm',
          titleClamp: '',
          deptClamp: '',
          userClamp: '',
          tagLimit: 3,
          checkboxClass: 'h-5 w-5',
        }
      case 'medium':
      default:
        return {
          tableClass: 'min-w-0',
          rowClass: '',
          headerPadding: 'px-3 py-2',
          cellPadding: 'px-3 py-2',
          fontSize: 'text-xs',
          headerFontSize: 'text-xs',
          badgePadding: 'px-1.5 py-0.5',
          badgeFontSize: 'text-xs',
          titleClamp: clamp('max-w-md'),
          deptClamp: clamp('max-w-[160px]'),
          userClamp: clamp('max-w-[180px]'),
          tagLimit: 1,
          checkboxClass: 'h-4 w-4',
        }
    }
  }

  const tableStyles = getTableStyles(tableSize)

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tickets...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-red-900">Error loading tickets</h3>
          <p className="mt-2 text-sm text-red-500">{error}</p>
          <button 
            className="mt-4 btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">
            {searchParams.get('assigned') === 'me' ? 'My Tickets' : 'Support Tickets'}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {searchParams.get('assigned') === 'me' 
              ? 'Tickets assigned to you' 
              : 'Manage and track support requests'
            }
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
          <button 
            className="btn-secondary"
            onClick={handleRefresh}
          >
            Refresh
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/tickets/advanced')}
          >
            Advanced Ticket
          </button>
          <button 
            className="btn-primary"
            onClick={handleNewTicket}
          >
            New Ticket
          </button>
        </div>
      </div>

      {/* Table toolbar: search (left) + filters + match count */}
      <div className="mt-6 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:w-72">
            <label htmlFor="ticket-search" className="sr-only">Search tickets</label>
            <input
              id="ticket-search"
              type="text"
              className="input-field w-full text-xs"
              placeholder="Search title or ticket #..."
            value={ticketSearch}
            onChange={(e) => {
              markFiltersCustom()
              setTicketSearch(e.target.value)
            }}
            />
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="w-full sm:w-44">
              <label htmlFor="ticket-view" className="sr-only">Saved view</label>
              <select
                id="ticket-view"
                className="input-field w-full text-xs"
                value={activeViewId}
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) return
                  if (isBuiltInTicketViewId(value)) applyBuiltInView(value)
                }}
              >
                <option value="">View…</option>
                {BUILT_IN_TICKET_VIEWS.filter(view => !view.staffOnly || isStaff).map(view => (
                  <option key={view.id} value={view.id}>
                    {view.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={openFiltersModal}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              Filters & sorting
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-violet-600 px-1.5 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear all
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 sm:ml-auto">
            <span className="font-medium text-gray-900">{filteredTickets.length}</span>
            {' of '}
            <span className="font-medium text-gray-900">{tickets.length}</span>
            {' tickets'}
          </p>
        </div>

        {showActiveFilterPills && activeFilterPills.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterPills.map(pill => (
              <span
                key={pill.id}
                className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 py-1 pl-3 pr-1 text-xs font-medium text-violet-900"
              >
                {pill.label}
                <button
                  type="button"
                  onClick={() => removeFilterPill(pill.remove)}
                  className="rounded-full p-0.5 text-violet-500 hover:bg-violet-100 hover:text-violet-800"
                  aria-label={`Remove ${pill.label} filter`}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <TicketFiltersModal
        isOpen={showFiltersModal}
        draft={filterDraft}
        onChange={setFilterDraft}
        onClose={() => setShowFiltersModal(false)}
        onApply={applyFilterDraft}
        onClearAll={clearFilterDraft}
        matchCount={draftMatchCount}
        totalCount={tickets.length}
        isStaff={!!isStaff}
        statusOptions={availableStatuses.map(status => ({
          id: status.id,
          label: status.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        }))}
        departmentOptions={departments.map(d => ({ id: d.id, label: d.name }))}
        sourceOptions={[
          { id: NO_SOURCE, label: 'No source' },
          ...sources.map(s => ({ id: s.id, label: s.name })),
        ]}
        assigneeOptions={[
          { id: UNASSIGNED_ASSIGNEE, label: 'Unassigned' },
          ...staffUsers.map(s => ({
            id: s.id,
            label: `${s.firstName} ${s.lastName}`,
          })),
        ]}
        organizationOptions={clientOrganizations
          .filter(org => org.isActive)
          .map(org => ({ id: org.id, label: org.name }))}
        clientUserOptions={draftClientUsers.map(u => ({
          id: u.id,
          label: `${u.firstName} ${u.lastName}`,
        }))}
      />

      {selectedTickets.length > 0 && (
        <div className="mt-6 card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedTickets.length} ticket{selectedTickets.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <div>
              <button 
                className="btn-secondary"
                onClick={() => handleBulkOperation('open')}
              >
                Bulk Actions
              </button>
            </div>
          </div>
        </div>
      )}
      
      {filteredTickets.length === 0 ? (
        <div className="mt-4 card">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">
              {tickets.length === 0 ? 'No tickets yet' : 'No tickets match your filters'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {tickets.length === 0 
                ? 'Get started by creating your first support ticket.'
                : 'Try adjusting your filter criteria.'
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-4 card">
          <div className="overflow-x-auto">
            <table className={`w-full divide-y divide-gray-200 ${tableStyles.tableClass}`}>
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider w-12`}>
                    <input
                      type="checkbox"
                      checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                      onChange={handleSelectAll}
                      className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${tableStyles.checkboxClass}`}
                    />
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Ticket
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Status
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Priority
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Department
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Source
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Created
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Tags
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Est. Hours
                  </th>
                  <th className={`${tableStyles.headerPadding} text-left ${tableStyles.headerFontSize} font-medium text-gray-500 uppercase tracking-wider`}>
                    Assigned To
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTickets.map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    className={`hover:bg-gray-50 ${tableStyles.rowClass}`}
                  >
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap`}>
                      <input
                        type="checkbox"
                        checked={selectedTickets.includes(ticket.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleTicketSelect(ticket.id)
                        }}
                        className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${tableStyles.checkboxClass}`}
                      />
                    </td>
                    <td className={`${tableStyles.cellPadding} cursor-pointer`} onClick={() => navigate(`/tickets/${ticket.id}`)}>
                      <div>
                        <div className={`${tableStyles.fontSize} font-medium text-gray-900 ${tableStyles.titleClamp}`}>
                          {ticket.title}
                        </div>
                        <div className={`${tableStyles.fontSize} text-gray-500`}>
                          {ticket.ticketNumber}
                        </div>
                      </div>
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap`}>
                      <span className={`inline-flex ${tableStyles.badgePadding} ${tableStyles.badgeFontSize} font-semibold rounded-full ${getStatusColor(ticket.status.name)}`}>
                        {ticket.status.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap`}>
                      <span className={`inline-flex ${tableStyles.badgePadding} ${tableStyles.badgeFontSize} font-semibold rounded-full ${getPriorityColor(ticket.priority.name)}`}>
                        {ticket.priority.name.charAt(0).toUpperCase() + ticket.priority.name.slice(1)}
                      </span>
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap ${tableStyles.fontSize} text-gray-900`}>
                      <div className={`${tableStyles.deptClamp}`} title={ticket.department.name}>
                        {ticket.department.name}
                      </div>
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap`}>
                      {ticket.source ? (
                        <span 
                          className={`inline-flex ${tableStyles.badgePadding} ${tableStyles.badgeFontSize} font-semibold rounded-full`}
                          style={{ 
                            backgroundColor: ticket.source.color ? `${ticket.source.color}20` : '#E5E7EB',
                            color: ticket.source.color || '#6B7280'
                          }}
                        >
                          {ticket.source.name}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap ${tableStyles.fontSize} text-gray-500`}>
                      {formatDate(ticket.createdAt)}
                    </td>
                    <td className={`${tableStyles.cellPadding} ${tableStyles.fontSize} text-gray-900`}>
                      {ticket.tags && ticket.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {ticket.tags.slice(0, tableStyles.tagLimit).map((tag, index) => (
                            <span
                              key={index}
                              className={`inline-flex items-center rounded-full font-medium bg-blue-100 text-blue-800 ${tableStyles.badgePadding} ${tableStyles.badgeFontSize}`}
                            >
                              {tag}
                            </span>
                          ))}
                          {ticket.tags.length > tableStyles.tagLimit && (
                            <span className={`${tableStyles.badgeFontSize} text-gray-500`}>+{ticket.tags.length - tableStyles.tagLimit}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className={`${tableStyles.cellPadding} whitespace-nowrap ${tableStyles.fontSize} text-gray-900`}>
                      {ticket.estimatedHours ? (
                        <span className="font-medium">{ticket.estimatedHours}h</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className={`${tableStyles.cellPadding} ${tableStyles.fontSize} text-gray-900`}>
                      {ticket.assignedUser ? (
                        <div className={`${tableStyles.userClamp}`} title={`${ticket.assignedUser.firstName} ${ticket.assignedUser.lastName}`}>
                          {ticket.assignedUser.firstName} {ticket.assignedUser.lastName}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Operations Modal */}
      <BulkOperationsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedTickets={selectedTickets}
        onSuccess={handleBulkSuccess}
      />
    </div>
  )
}

export default Tickets
